import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsAPI } from '@matix/api-client';
import {
  ReportToolbar, Field, DateTimeRange, fmtMoney, fmtInt,
  todayFrom, todayTo, rangeParams, rangeLabel, useLabelOf, printableHtml,
} from './reportShared';
import { printReport, type ReportPaper } from './reportPrint';
import { ReportSheetBuilder, downloadReportExcel } from './reportExcel';
import { useLang } from '../../lib/language';

export function QuickSalesReport(): JSX.Element {
  const { t, lang } = useLang();
  const labelOf = useLabelOf();
  const printRef = useRef<HTMLDivElement>(null);
  const [branchId, setBranchId] = useState<number | ''>('');
  // '' = all cashiers (the default).
  const [cashierId, setCashierId] = useState<number | ''>('');
  const [from, setFrom] = useState(todayFrom);
  const [to, setTo] = useState(todayTo);
  const [params, setParams] = useState<
    { branch_id: number; from: string; to: string; from_time?: string; to_time?: string; cashier_id?: number } | null
  >(null);

  const filtersQ = useQuery({ queryKey: ['rpt-filters'], queryFn: () => reportsAPI.filters() });
  // Quick Sales is the till's OWN branch summary (2026-09-08): the picker
  // offers only the branch the cashier signed in to, preselected. The server
  // pins the query to that branch too. A token with no branch (should not
  // happen on a cashier login) keeps the full list so the report still runs.
  const ownBranchId = filtersQ.data?.default_branch_id ?? null;
  const branches = useMemo(
    () => (filtersQ.data?.branches ?? []).filter((b) => ownBranchId == null || b.id === ownBranchId),
    [filtersQ.data, ownBranchId],
  );
  useEffect(() => {
    if (filtersQ.data && branchId === '') {
      setBranchId(ownBranchId ?? branches[0]?.id ?? '');
    }
  }, [filtersQ.data, branchId, ownBranchId, branches]);

  const dataQ = useQuery({
    queryKey: ['quick-sales', params],
    queryFn: () => reportsAPI.quickSales(params!),
    enabled: params != null,
  });

  const range = rangeLabel(from, to);
  const title = t({ en: 'Quick Sales Report', ar: 'تقرير ملخص المبيعات' });

  function run(): void {
    const r = rangeParams(from, to);
    if (branchId === '' || !r) return;
    setParams({ branch_id: branchId, ...r, ...(cashierId !== '' ? { cashier_id: cashierId } : {}) });
  }
  function exportPdf(paper: ReportPaper): void {
    printReport(title, range, printableHtml(printRef), paper);
  }

  const d = dataQ.data;
  const branchName = filtersQ.data?.branches.find((b) => b.id === branchId);
  const cashier = params?.cashier_id != null ? filtersQ.data?.sales_cashiers?.find((c) => c.id === params.cashier_id) : undefined;
  const scope = labelOf(branchName ?? { name: '', name_ar: null }) + (cashier ? ` · ${labelOf(cashier)}` : '');

  function exportExcel(): void {
    if (!d) return;
    const cols = [
      { label: t({ en: 'Type', ar: 'النوع' }) },
      { label: t({ en: 'Qty', ar: 'الكمية' }), kind: 'int' as const },
      { label: t({ en: 'Amount', ar: 'المبلغ' }), kind: 'money' as const },
    ];
    const sum = (rows: { count: number; amount: number }[]) => [
      rows.reduce((s, r) => s + r.count, 0),
      rows.reduce((s, r) => s + r.amount, 0),
    ];
    const sheet = new ReportSheetBuilder()
      .title(title, `${scope} · ${range}`)
      .section(t({ en: 'Payment Types', ar: 'أنواع الدفع' }))
      .table(cols, d.payment_types.map((r) => [r.metric, r.count, r.amount]), {
        totals: [t({ en: 'Total', ar: 'الإجمالي' }), ...sum(d.payment_types)],
        emptyText: t({ en: 'No data', ar: 'لا توجد بيانات' }),
      })
      .section(t({ en: 'Order Types', ar: 'أنواع الطلبات' }))
      .table(cols, d.order_types.map((r) => [r.metric, r.count, r.amount]), {
        totals: [t({ en: 'Total', ar: 'الإجمالي' }), ...sum(d.order_types)],
        emptyText: t({ en: 'No data', ar: 'لا توجد بيانات' }),
      })
      .section(t({ en: 'Totals', ar: 'الإجماليات' }))
      .table(cols, [
        [t({ en: 'Total Sales', ar: 'إجمالي المبيعات' }), d.total_sales.count, d.total_sales.amount],
        [t({ en: 'Total Returns', ar: 'إجمالي المرتجعات' }), d.total_returns.count, d.total_returns.amount],
      ], {
        header: false,
        totals: [t({ en: 'Net Sales', ar: 'صافي المبيعات' }), null, d.net_sales],
      })
      .build(title, { rightToLeft: lang === 'ar' });
    downloadReportExcel(`${title} ${range}`, sheet);
  }

  return (
    <div className="rpt-wrap">
      <ReportToolbar
        running={dataQ.isFetching}
        canExport={!!d}
        onRun={run}
        onExport={exportPdf}
        onExportExcel={exportExcel}
        filters={
          <>
            <Field label={t({ en: 'Branch', ar: 'الفرع' })}>
              <select value={branchId} onChange={(e) => setBranchId(e.target.value ? Number(e.target.value) : '')}>
                {branches.map((b) => <option key={b.id} value={b.id}>{labelOf(b)}</option>)}
              </select>
            </Field>
            <DateTimeRange from={from} to={to} onFrom={setFrom} onTo={setTo} />
            <Field label={t({ en: 'Cashier', ar: 'الكاشير' })}>
              <select value={cashierId} onChange={(e) => setCashierId(e.target.value ? Number(e.target.value) : '')}>
                <option value="">{t({ en: 'All', ar: 'الكل' })}</option>
                {(filtersQ.data?.sales_cashiers ?? []).map((c) => <option key={c.id} value={c.id}>{labelOf(c)}</option>)}
              </select>
            </Field>
          </>
        }
      />

      {dataQ.error && <div className="rpt-error">{t({ en: 'Failed to load report. Try again.', ar: 'تعذّر تحميل التقرير. حاول مجدداً.' })}</div>}

      <div className="rpt-print" ref={printRef}>
        {d && (
          <>
            <div className="rpt-meta">{scope} · {range}</div>

            <h2 className="section">{t({ en: 'Payment Types', ar: 'أنواع الدفع' })}</h2>
            <MetricTable rows={d.payment_types} />

            <h2 className="section">{t({ en: 'Order Types', ar: 'أنواع الطلبات' })}</h2>
            <MetricTable rows={d.order_types} />

            <h2 className="section">{t({ en: 'Totals', ar: 'الإجماليات' })}</h2>
            <table>
              <tbody>
                <tr><td>{t({ en: 'Total Sales', ar: 'إجمالي المبيعات' })}</td><td className="num">{fmtInt(d.total_sales.count)}</td><td className="num">{fmtMoney(d.total_sales.amount)}</td></tr>
                <tr><td>{t({ en: 'Total Returns', ar: 'إجمالي المرتجعات' })}</td><td className="num">{fmtInt(d.total_returns.count)}</td><td className="num">{fmtMoney(d.total_returns.amount)}</td></tr>
                <tr className="total-row"><td>{t({ en: 'Net Sales', ar: 'صافي المبيعات' })}</td><td className="num" /><td className="num">{fmtMoney(d.net_sales)}</td></tr>
              </tbody>
            </table>
          </>
        )}
        {!d && !dataQ.isFetching && <div className="rpt-empty">{t({ en: 'Choose a branch, a date and time range, then “Show report”.', ar: 'اختر الفرع والتاريخ والوقت، ثم “عرض التقرير”.' })}</div>}
      </div>
    </div>
  );
}

function MetricTable({ rows }: { rows: { metric: string; count: number; amount: number }[] }): JSX.Element {
  const { t } = useLang();
  const totalCount = rows.reduce((s, r) => s + r.count, 0);
  const totalAmt = rows.reduce((s, r) => s + r.amount, 0);
  return (
    <table>
      <thead><tr><th>{t({ en: 'Type', ar: 'النوع' })}</th><th className="num">{t({ en: 'Qty', ar: 'الكمية' })}</th><th className="num">{t({ en: 'Amount', ar: 'المبلغ' })}</th></tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={3} className="rpt-empty">{t({ en: 'No data', ar: 'لا توجد بيانات' })}</td></tr>}
        {rows.map((r) => (
          <tr key={r.metric}><td>{r.metric}</td><td className="num">{fmtInt(r.count)}</td><td className="num">{fmtMoney(r.amount)}</td></tr>
        ))}
      </tbody>
      {rows.length > 0 && (
        <tfoot><tr><td>{t({ en: 'Total', ar: 'الإجمالي' })}</td><td className="num">{fmtInt(totalCount)}</td><td className="num">{fmtMoney(totalAmt)}</td></tr></tfoot>
      )}
    </table>
  );
}
